import { Box, Flex, Text, Image, Alert, Button } from '@chakra-ui/react'
import { ChakraPrimaryButton } from 'components/Button'
import {
  initialLabState,
  useLabSchedulingContext,
} from 'context/LabSchedulingContext/LabSchedulingProvider'
import { useLabScheduling } from 'hooks/useLabScheduling/useLabscheduling'
import alertUrl from 'assets/images/alert-info.svg'
import imageSrc from 'assets/images/lab-calendar.svg'
import confirmImageSrc from 'assets/images/check-yellow.svg'
import LoadingState from 'components/Spinner'
import { Amplitude } from 'utils/amplitude.utils'
import LabCorpLogo from 'assets/images/LabCorpLogo.png'
import { LabProvider, LabSchedulingSource } from 'utils/amplitude.types'
import { useLabSchedulingError } from 'hooks/useLabScheduling/useLabSchedulingError'
import { formatDateTime } from 'utils/time.utils'
import useSearchParams from 'hooks/useSearchParams'

const ModifyLabAppointment = () => {
  const {
    setCurrentScreen,
    labState,
    setIsModifyFlow,
    selectedLab,
    labAppointmentState,
    amplitudeLabSchedulingMetadata,
    updateAmplitudeLabSchedulingMetadata,
    setLabState,
  } = useLabSchedulingContext()
  const { useCancelAppointment } = useLabScheduling()
  const {
    mutateAsync: cancelAppointment,
    isLoading: isLoadingCancelAppointment,
  } = useCancelAppointment()
  const { handleLabSchedulingError } = useLabSchedulingError()
  const searchParams = useSearchParams()
  const isRedirect = searchParams.get('isRedirect') === 'true'

  const {
    selectedLabAddress,
    selectedDate,
    selectedTime,
    selectedLabId,
    labTimezone,
  } = labState
  const { confirmationNumber } = labAppointmentState
  const dateTime = `${selectedDate}T${selectedTime}`

  const formattedDate = formatDateTime(dateTime, labTimezone)

  const rescheduleHandler = () => {
    Amplitude.labScheduleStart({
      source: LabSchedulingSource.RESCHEDULE,
      patient_action_id: amplitudeLabSchedulingMetadata.patient_action_id ?? '',
      is_reschedule: true,
      lab_appointment_id:
        amplitudeLabSchedulingMetadata.lab_appointment_id ?? '',
    })
    updateAmplitudeLabSchedulingMetadata({
      source: LabSchedulingSource.RESCHEDULE,
    })
    setLabState({
      selectedLabAddress: initialLabState.selectedLabAddress,
      selectedLabId: initialLabState.selectedLabId,
      selectedDate: initialLabState.selectedDate,
      selectedTime: initialLabState.selectedTime,
    })
    setIsModifyFlow(true)
    setCurrentScreen('timeslotMap')
  }

  const cancelHandler = async () => {
    try {
      const cancelData = {
        selectedLab,
        confirmationNumber,
      }

      const lab =
        selectedLab === 'LabCorp' ? LabProvider.LABCORP : LabProvider.QUEST
      Amplitude.labCancelStart({
        lab_location: selectedLabAddress,
        lab_code: selectedLabId,
        lab_appointment_id:
          amplitudeLabSchedulingMetadata.lab_appointment_id ?? '',
        lab_provider: lab,
        lab_appointment_time:
          amplitudeLabSchedulingMetadata.lab_appointment_time,
      })

      const result = await cancelAppointment(cancelData)

      if (!!result.confirmationNumber) {
        Amplitude.labCancelDone({
          lab_location: selectedLabAddress,
          lab_code: selectedLabId,
          lab_appointment_id:
            amplitudeLabSchedulingMetadata.lab_appointment_id ?? '',
          lab_provider: lab,
          lab_appointment_time:
            amplitudeLabSchedulingMetadata.lab_appointment_time,
        })
        setCurrentScreen('cancellation')
      }
    } catch (error) {
      handleLabSchedulingError(error, 'Failed to cancel appointment')
    }
  }

  const titleText = isRedirect
    ? 'Your existing appointment'
    : 'Modify your appointment'

  return (
    <Box bg="white" p={8} borderRadius="lg" boxShadow="lg" width="sm">
      <Flex flexDirection="column" alignItems="center" justifyContent="center">
        <Image
          src={isRedirect ? confirmImageSrc : imageSrc}
          marginBottom="1rem"
        />
        <Text fontFamily="Instrument Serif" fontSize="1.5rem">
          {titleText}
        </Text>

        {isRedirect && (
          <Text>
            You already have an upcoming appointment booked, but please modify
            it here if needed.
          </Text>
        )}

        <Flex
          flexDirection="column"
          alignItems="flex-start"
          width="100%"
          marginBottom="2rem"
        >
          <Text fontWeight="500" color="#161E35" marginBottom=".5rem">
            Lab Location
          </Text>
          <Flex width="100%" justifyContent="space-between">
            <Text margin="0">{selectedLabAddress}</Text>
          </Flex>
        </Flex>

        <Flex
          flexDirection="column"
          alignItems="flex-start"
          width="100%"
          marginBottom="2rem"
        >
          <Text fontWeight="500" color="#161E35" marginBottom=".5rem">
            Date and time
          </Text>
          <Flex width="100%" justifyContent="space-between">
            <Text margin="0">{formattedDate}</Text>
          </Flex>
        </Flex>

        {isLoadingCancelAppointment && <LoadingState />}
        {!isLoadingCancelAppointment && (
          <>
            <ChakraPrimaryButton
              width="100%"
              onClick={() => rescheduleHandler()}
              styleProps={{ marginBottom: '1rem' }}
            >
              Reschedule appointment
            </ChakraPrimaryButton>
            <Button
              variant="outline"
              width="100%"
              height="36px"
              borderRadius="8px"
              borderColor="#141743"
              color="#1F2937"
              onClick={cancelHandler}
              fontSize="14px"
              fontWeight="600"
              _hover={{ bg: 'transparent', borderColor: '#9CA3AF' }}
            >
              Cancel appointment
            </Button>
          </>
        )}
        {selectedLab === 'LabCorp' && (
          <Image src={LabCorpLogo} width="90px" marginTop="2.5rem" />
        )}
        <Alert backgroundColor="#E8EFF9" marginTop="1.5rem" color="#23253D">
          <Image src={alertUrl} marginRight=".675rem" alignSelf="flex-start" />
          <Flex flexDirection="column">
            <Text margin="0" fontSize=".75rem">
              If you cancelled and rebooked your appointment directly with the
              lab, then that change may not be reflected here.
            </Text>
            <Text margin="0" fontSize=".75rem" fontWeight="600">
              You can cancel this appointment and keep your new one.
            </Text>
          </Flex>
        </Alert>
      </Flex>
    </Box>
  )
}

export default ModifyLabAppointment
